import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withCtx as _withCtx, unref as _unref, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "overlay"
}
const _hoisted_2 = { key: 1 }

import {
  ref,
  computed,
  onUnmounted,
  watch,
  shallowRef,
  onMounted,
  nextTick,
  StyleValue,
  PropType,
  toRefs,
} from "vue";
import * as pdfjsLib from "pdfjs-dist";
import { getIcon } from "@/helpers/api";
import {
  PDFDocumentProxy,
  PDFPageProxy,
  PageViewport,
  AnnotationLayer,
} from "pdfjs-dist";

import { PDFLinkService, LinkTarget } from "pdfjs-dist/web/pdf_viewer.js";
import { emptyElement, releaseChildCanvases } from "./util";
import CustomZoomer from "@/globals/modules/CustomZoomer/CustomZoomer.vue";
import { spinnerManager } from "@/globals/pages/spinnerControl";
import pdfjsWorker from "pdfjs-dist/webpack";
import { IDownloadManager } from "pdfjs-dist/types/web/interfaces";
import type { IControls, IEmits } from './model'
import { useControls } from "@/globals/components/PdfViewer/useControls";
import PdfControls from "@/globals/components/PdfViewer/PdfControls.vue";
import { IFont, getFont } from "@/helpers/fonts";
import { useDeviceStatus } from "@/stores/deviceStatus";

enum State {
  FLOW = "flow",
  CENTERED = "centered",
}

const MAX_TRIES = 50;

export default /*@__PURE__*/_defineComponent({
  __name: 'PdfViewer',
  props: {
  source: { type: String, required: true },
  zoomable: { type: Boolean, default: true },
  quality: { type: Number, default: 1 },
  fullSize: { type: Boolean, default: false },
  isScroll: { type: Boolean, default: true },
  page: { type: Number, default: undefined },
  rotation: { type: Number, default: 0 },
  controlOptions: { type: Object as PropType<IControls>, default: () => { return { isMove: false, isDelete: false, isDownload: false } } },
  noShow: { type: Boolean, default: false },
},
  emits: ["loaded", "internalLinkClicked", "click", "isZoomed", "delete-file", "download-file", "page-changed"],
  setup(__props, { emit: __emit }) {

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;


const props = __props;

const emits = __emit;

const { isWeb } = toRefs(useDeviceStatus());
const { isMove, events } = useControls();
const state = ref<State>(State.FLOW);
const pdfContainerRef = ref<HTMLDivElement>();
const binIcon = ref<string>();
const downloadIcon = ref<string>();

const isControl = computed(() => Object.keys(props.controlOptions).some(key => props.controlOptions[key as keyof IControls]));
const currentPage = ref(props.page || 1);

watch(
  () => props.source,
  (newSource, oldSource) => {
    if (newSource !== oldSource) {
      currentPage.value = props.page || 1;
    }
  }
);

const next = () => {
  if (!isRightArrowGray.value) {
    currentPage.value = Math.min(currentPage.value + 1, pageCount.value ?? 1);
    emits("page-changed", currentPage.value);
    console.log("Next")
  }
};

const prev = () => {
  if (!isLeftArrowGray.value) {
    currentPage.value = Math.max(currentPage.value - 1, 1);
    emits("page-changed", currentPage.value);
  }
};

const isLeftArrowGray = computed(() => currentPage.value === 1);
const isRightArrowGray = computed(() => currentPage.value === pageCount.value);
const deleteFile = () => emits("delete-file");
const downloadFile = () => emits("download-file");

const nore12 = ref<StyleValue>();

const fixedWidthRef = ref<HTMLDivElement | null>(null);
const canvasContainerRef = ref<HTMLDivElement | null>(null);
const textLayerRef = ref<HTMLDivElement | null>(null);
const annotationLayerRef = ref<HTMLDivElement | null>(null);

const pageCount = ref<number | null>(null);
let pdfDocument: PDFDocumentProxy | null = null;
const internalViewport = ref<PageViewport | null>(null);
const fitMode = ref<"width" | "height">("width");

const pageHeight = ref<number>();
const pageWidth = ref<number>();
const uniqueKey = computed(() => `${props.source}-${Date.now()}`);
const isLoaded = ref(false);
const leftArrow = ref<string>();
const rightArrow = ref<string>();
onMounted(async () => {
  nore12.value = await getFont("nore12") as StyleValue;

  leftArrow.value = await getIcon("1602", "previous_page");
  rightArrow.value = await getIcon("1602", "next_page");
  binIcon.value = await getIcon("2105", "bin");
  downloadIcon.value = await getIcon("2105", "download");

  await nextTick();

  await updateDimensions();

  if (scrollContainerDimensions.value.width > 0) {
    initViewer(scrollContainerDimensions.value.width, scrollContainerDimensions.value.height);
  } else {
    tryToSyncScrollContainerDimensions();
  }

});

function scrollEvent(e: Event) {
  if (isZoomed.value) {
    e.preventDefault();
  }
}

const getWrapperStyles = computed<StyleValue>(() => {
  if (isLoaded.value) {
    return "" as StyleValue;
  } else {
    return {
      height: "100%",
      width: "100%",
    } as StyleValue;
  }
});

const isZoomed = ref(false);
function setIsZoomed(v: boolean) {
  isZoomed.value = v;
  emits("isZoomed", v);
}
const vZoomerRef = shallowRef<any>(null);

watch(
  computed(() => props.source),
  async (newSrc, oldSrc) => {
    tryToSyncScrollContainerDimensions();
  }
);

// scrollContainer can be mounted but its computed width can still be zero
const scrollContainer = ref<HTMLDivElement | null>(null);
const scrollContainerDimensions = ref({
  width: 0,
  height: 0,
});

const wrapperRef = ref<HTMLDivElement | null>(null);

const updateDimensions = async () => {
  if (!scrollContainer.value) return;

  const sc = scrollContainer.value;
  const wrapper = wrapperRef.value;

  const width = sc.clientWidth || sc.getBoundingClientRect().width;


  let height =
    wrapper?.clientHeight || wrapper?.getBoundingClientRect().height || 0;


  if (height === 0) {
    height = window.innerHeight;
  }

  if (width > 0 && height > 0) {
    scrollContainerDimensions.value = { width, height: height/2 };
    console.log("[PDF] Dimensões atualizadas (viewport):", scrollContainerDimensions.value);
  } else {
    console.log(
      "[PDF] scrollContainer ainda não está dimensionado corretamente (viewport):",
      width,
      height
    );
  }
};



watch(
  [
    scrollContainerDimensions,
    computed(() => props.page),
    computed(() => props.source),
  ],
  async (nv, ov) => {
    if (
      scrollContainerDimensions.value.height > 0 &&
      scrollContainerDimensions.value.width > 0
    ) {
      await initViewer(
        scrollContainerDimensions.value.width,
        scrollContainerDimensions.value.height
      );
    } else {
      tryToSyncScrollContainerDimensions();
    }
  }
);

let isSyncing = false;
function tryToSyncScrollContainerDimensions() {
  if (isSyncing) {
    return;
  }
  isSyncing = true;
  let tries = 0;
  const interval = setInterval(() => {
    updateDimensions();
    if (scrollContainerDimensions.value.width > 0) {
      clearInterval(interval);
      isSyncing = false;
      initViewer(
        scrollContainerDimensions.value.width,
        scrollContainerDimensions.value.height
      );
    } else {
      tries++;
      if (tries > MAX_TRIES) {
        isSyncing = false;
        clearInterval(interval);
        console.error("Falha ao sincronizar as dimensões do scrollContainer.");
      }
    }
  }, 100);
}

let lock = false;
async function initViewer(clientWidth: number, clientHeight: number) {
  console.log('Inicializando viewer com width:', clientWidth, 'height:', clientHeight);
  if (lock) {
    return;
  }

  lock = true;
  cleanUp();
  spinnerManager.registerTask("pdfViewer");
  try {
    const pdfAttributes = await init({
      src: props.source,
      clientWidth,
      clientHeight,
    });

    fixedWidthRef.value?.style.setProperty(
      "--scale-factor",
      `${pdfAttributes.internalViewport.scale}`
    );

    pageCount.value = pdfAttributes.pagesAmount;
    pageWidth.value = pdfAttributes.pageDimensions.width;
    pageHeight.value = pdfAttributes.pageDimensions.height;
    internalViewport.value = pdfAttributes.internalViewport;

    isLoaded.value = true;
    console.log('PDF carregado com sucesso.');

    await nextTick();
    await nextTick();

    if (pdfContainerRef.value) {
      await render({
        pdfDocument: pdfAttributes.pdfDocument,
        pageCount: pdfAttributes.pagesAmount,
        pdfContainerRef: pdfContainerRef.value,
        pageWidth: pageWidth.value,
        pageHeight: pageHeight.value,
        internalViewport: internalViewport.value,
        pageNr: props.page,
      });
      // Forçar uma atualização adicional após a renderização
      await nextTick();
      await nextTick();
    } else {
      console.error("pdfContainerRef está indefinido! - pdfViewer.vue");
    }
  } catch (e) {
    console.warn('Erro no initViewer:', e);
    spinnerManager.unregisterTask("pdfViewer");
    return;
  }

  spinnerManager.unregisterTask("pdfViewer");
  lock = false;
}




function throttle<T extends (...args: any[]) => any>(
  f: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => f(...args), delay);
  };
}

const resizeObserver = new ResizeObserver(
  throttle(() => {
    updateDimensions();
    if (scrollContainerDimensions.value.width > 0 && scrollContainerDimensions.value.height > 0) {
      initViewer(scrollContainerDimensions.value.width, scrollContainerDimensions.value.height);
    }
  }, 100)
);

onMounted(() => {
  if (scrollContainer.value) {
    resizeObserver.observe(scrollContainer.value);
  }
});

onUnmounted(() => {
  cleanUp();
  resizeObserver.disconnect();
});


function cleanUp() {
  if (pdfContainerRef.value) {
    releaseChildCanvases(pdfContainerRef.value);
    pdfContainerRef.value.innerHTML = "";
  }

  resizeObserver.disconnect();
  isLoaded.value = false;
}

async function init(params: {
  src: string;
  clientWidth: number;
  clientHeight: number;
}): Promise<{
  pageDimensions: { width: number; height: number };
  pagesAmount: number;
  internalViewport: PageViewport;
  pdfDocument: PDFDocumentProxy;
}> {
  const { clientWidth, clientHeight, src } = params;

  let pageWidth = 0;
  let pageHeight = 0;
  let pageCount = 0;
  let internalViewport: PageViewport | null = null;

  const documentLoadingTask = pdfjsLib.getDocument(src);
  pdfDocument = await documentLoadingTask.promise;
  pageCount = pdfDocument.numPages;
  const page = await pdfDocument.getPage(1);

  const pageSizes = loadAvailableDimensions(page, clientWidth, clientHeight);
  pageWidth = pageSizes.pageWidth;
  pageHeight = pageSizes.pageHeight;
  if (pageWidth === 0 || pageHeight === 0) {
    throw new Error("pageWidth or pageHeight is 0!");
  }
  internalViewport = loadViewport(page, pageWidth);

  events.loadedPdf(pdfDocument);
  emits("loaded", pdfDocument);

  if (internalViewport === null) {
    throw new Error("internalViewport is null!");
  }
  return {
    pageDimensions: { width: pageWidth, height: pageHeight },
    pagesAmount: pageCount,
    internalViewport: internalViewport,
    pdfDocument: pdfDocument,
  };
}

function loadViewport(page: PDFPageProxy, availableWidth: number) {
  return page.getViewport({
    scale: availableWidth / page.view[2],
  });
}

const refit = async () => {
  if (
    scrollContainerDimensions.value.width > 0 &&
    scrollContainerDimensions.value.height > 0
  ) {
    purgeViewerDisplay();
    await initViewer(
      scrollContainerDimensions.value.width,
      scrollContainerDimensions.value.height
    );
  } else {
    tryToSyncScrollContainerDimensions();
  }
};
const fitToHeight = async () => {
  console.log('[PDF] fitToHeight CLICK');
  fitMode.value = "height";
  await refit();
};

const fitToWidth = async () => {
  console.log('[PDF] fitToWidth CLICK');
  fitMode.value = "width";
  await refit();
};

function loadAvailableDimensions(
  page: PDFPageProxy,
  clientWidth: number,
  clientHeight: number
) {
  // fit to HEIGHT
  if (fitMode.value === "height" && clientHeight > 0) {
    const height = clientHeight;
    const width = Math.floor(page.view[2] * (height / page.view[3]));
    return {
      pageWidth: width,
      pageHeight: height,
    };
  }

  // fit to WIDTH (comportamento original)
  const width = clientWidth;
  const height = Math.floor(page.view[3] * (width / page.view[2]));
  return {
    pageWidth: width,
    pageHeight: height,
  };
}

function adjustPageNumber(pageNr: number, pageCount: number): number {
  if (pageNr < 1) {
    return pageCount - Math.abs(pageNr % pageCount);
  } else if (pageNr > pageCount) {
    return ((pageNr - 1) % pageCount) + 1;
  }
  return pageNr;
}

async function render(params: {
  pdfDocument: PDFDocumentProxy;
  pageCount: number;
  pdfContainerRef: HTMLDivElement;
  pageWidth: number;
  pageHeight: number;
  internalViewport: PageViewport;
  pageNr?: number;
}) {
  const {
    pdfDocument,
    pageCount,
    pdfContainerRef,
    pageWidth,
    pageHeight,
    internalViewport,
    pageNr,
  } = params;

  let startPage = 1;
  let endPage = pageCount;

  if (pageNr !== undefined) {
    const adjustedPageNr = adjustPageNumber(pageNr, pageCount);
    startPage = adjustedPageNr;
    endPage = adjustedPageNr;
  }

  try {
    for (let i = startPage; i <= endPage; i++) {
      const page = await pdfDocument.getPage(i);
      const pageContainer = document.createElement("div");
      pageContainer.style.width = `${pageWidth}px`;
      pageContainer.style.height = `${pageHeight}px`;
      pageContainer.style.position = "relative";

      const canvas = await renderPageCanvas(
        page,
        internalViewport,
        pageWidth,
        pageHeight
      );
      const textLayer = await renderPageTextLayer(page, internalViewport);
      const annotationLayer = await renderPageAnnotationLayer(
        page,
        internalViewport
      );

      pageContainer.appendChild(canvas);
      pageContainer.appendChild(textLayer);
      pageContainer.appendChild(annotationLayer);

      pdfContainerRef.appendChild(pageContainer);
    }
  } catch (e) {
    console.error(e);
  }
}

async function renderPageAnnotationLayer(
  page: PDFPageProxy,
  internalViewport: PageViewport
) {
  const viewport = internalViewport.clone({
    dontFlip: true,
  });

  const pdfLinkService = new PDFLinkService();
  pdfLinkService.externalLinkTarget = LinkTarget.BLANK; // Abre links em nova aba

  const annotations = await page.getAnnotations();
  const annotationsPageDiv = document.createElement("div");
  annotationsPageDiv.classList.add("annotationLayer");

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  const layer = new AnnotationLayer({
    div: annotationsPageDiv,
    page: page,
    viewport: viewport,
  });

  const downloadManager: IDownloadManager = {
    downloadUrl: (url, filename): void => {
      console.warn("NOT IMPLEMENTED - downloadUrl", url, filename);
    },
    downloadData(data, filename, contentType): void {
      console.warn(
        "NOT IMPLEMENTED - downloadData",
        data,
        filename,
        contentType
      );
    },
    download(blob, url, filename): void {
      console.warn("NOT IMPLEMENTED - download", blob, url, filename);
    },
    openOrDownloadData(
      element: HTMLElement,
      data: Uint8Array,
      filename: string
    ): boolean {
      console.warn(
        "NOT IMPLEMENTED - openOrDownloadData",
        element,
        data,
        filename
      );
      return true;
    },
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  layer.render({
    annotations: annotations,
    div: annotationsPageDiv,
    linkService: pdfLinkService,
    page,
    enableScripting: true,
    renderForms: true,
    downloadManager: downloadManager,
    viewport: viewport,
  });
  return annotationsPageDiv;
}
async function renderPageTextLayer(
  page: PDFPageProxy,
  internalViewport: PageViewport
): Promise<HTMLDivElement> {
  const viewport = internalViewport.clone({
    dontFlip: true,
  });
  const textLayerDiv = document.createElement("div");
  textLayerDiv.classList.add("textLayer");
  textLayerDiv.style.position = "absolute";


  const textContent = await page.getTextContent();
  await pdfjsLib.renderTextLayer({
    container: textLayerDiv,
    textContentSource: textContent,
    viewport: viewport,

  }).promise;

  processTextLayerForLinks(textLayerDiv);

  return textLayerDiv;
}


function processTextLayerForLinks(textLayerDiv: HTMLDivElement) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const spans = Array.from(textLayerDiv.querySelectorAll("span"));

  spans.forEach(span => {
    const text = span.textContent;
    if (!text) return;

    const matches = text.match(urlRegex);
    if (matches) {
      const newHTML = text.replace(urlRegex, url => {
        return `<a href="${url}" target="_blank" style="color: inherit; text-decoration: none;">${url}</a>`;
      });

      span.innerHTML = newHTML;
    }
  });
}
async function renderPageCanvas(
  page: PDFPageProxy,
  internalViewport: PageViewport,
  pageWidth: number,
  pageHeight: number
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement("canvas");
  canvas.style.width = `${pageWidth}px`;
  canvas.style.height = `${pageHeight}px`;
  const width = pageWidth;

  const viewport = page.getViewport({
    scale: Math.ceil(width / internalViewport.width) + props.quality,
  });

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: canvas.getContext("2d")!,
    viewport: viewport,
  }).promise;
  return canvas;
}

const fixedWidth = computed<StyleValue>(() => {
  if (
    pageWidth.value &&
    pageWidth.value > 0 &&
    pageHeight.value &&
    pageHeight.value > 0 &&
    pageCount.value &&
    pageCount.value > 0
  ) {
    return {
      width: `${pageWidth.value}px`,
      height:
        props.page === undefined
          ? `${pageHeight.value * pageCount.value}px`
          : `${pageHeight.value}px`,
    } as StyleValue;
  } else {
    return {
      width: 'auto',
      height: '100%'
    } as StyleValue;
  }
});

const handleClick = (event: Event) => {
  if (props.zoomable) {
    event.stopPropagation();
  }
  emits("click", props.source);
};

// when the width of our container changes we want to reload
const availableWidth = ref<number>();
watch([availableWidth, isLoaded], async (nv, ov) => {
  const [oldSize, oldLoaded] = nv;
  const [newSize, newLoaded] = ov;

  if (oldSize !== newSize && newLoaded === true && newSize !== undefined) {
    purgeViewerDisplay();
    cleanUp();
    if (
      scrollContainerDimensions.value.height > 0 &&
      scrollContainerDimensions.value.width > 0
    ) {
      try {
        const pdfAttributes = await init({
          src: props.source,
          clientWidth: scrollContainerDimensions.value.width,
          clientHeight: scrollContainerDimensions.value.height,
        });
        fixedWidthRef.value?.style.setProperty(
          "--scale-factor",
          `${pdfAttributes.internalViewport.scale}`
        );
        pageCount.value = pdfAttributes.pagesAmount;
        pageWidth.value = pdfAttributes.pageDimensions.width;
        pageHeight.value = pdfAttributes.pageDimensions.height;
        internalViewport.value = pdfAttributes.internalViewport;

        render({
          pdfDocument: pdfAttributes.pdfDocument,
          pageCount: pdfAttributes.pagesAmount,
          pdfContainerRef: pdfContainerRef.value!,
          pageWidth: pageWidth.value!,
          pageHeight: pageHeight.value!,
          internalViewport: internalViewport.value!,
        });
      } catch (e) {
        console.warn(e);
      }
    } else {
      tryToSyncScrollContainerDimensions();
    }
  }
});

function purgeViewerDisplay() {
  // TODO: remove this method and extend cleanUp() method and call it instead
  isLoaded.value = false;

  cleanUp();
  if (annotationLayerRef.value) emptyElement(annotationLayerRef.value);
  if (textLayerRef.value) emptyElement(textLayerRef.value);
  if (canvasContainerRef.value) releaseChildCanvases(canvasContainerRef.value);

  pageWidth.value = undefined;
  pageHeight.value = undefined;

  internalViewport.value = null;
}
const getCanZoomWithMouseWheel = computed(() =>
  isZoomed.value ? true : false
);

const getIsScroll = computed(() =>
  isZoomed.value || !props.isScroll
    ? { overflow: "hidden" }
    : ""
);
const isCentered = computed(() => {
  if (fixedWidthRef.value && scrollContainer.value) {
    if (fixedWidthRef.value.clientHeight < scrollContainer.value.clientHeight) {
      return "centered-flex";
    } else {
      return "";
    }
  }
  return undefined;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "pdf-viewer",
    ref_key: "wrapperRef",
    ref: wrapperRef
  }, [
    (_openBlock(), _createBlock(PdfControls, {
      "no-show": props.noShow,
      key: uniqueKey.value,
      isMove: __props.controlOptions.isMove,
      isDelete: __props.controlOptions.isDelete,
      isDownload: __props.controlOptions.isDownload,
      isZoomToHeight: true,
      isZoomToWidth: true,
      isPdf: true,
      page: currentPage.value,
      pageCount: pageCount.value ?? 1,
      onDelete: deleteFile,
      onDownload: downloadFile,
      onPrev: prev,
      onNext: next,
      onExpand: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('click', __props.source))),
      onZoomToHeight: fitToHeight,
      onZoomToWidth: fitToWidth
    }, null, 8, ["no-show", "isMove", "isDelete", "isDownload", "page", "pageCount"])),
    _createElementVNode("div", {
      style: _normalizeStyle([getWrapperStyles.value, { overflowX: 'hidden', overflowY: 'visible', height: '100%' }])
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["scroll-container", { 'scroll-hidden': props.isScroll }]),
        style: _normalizeStyle([getIsScroll.value]),
        onScroll: scrollEvent,
        onTouchmove: scrollEvent,
        onClick: _cache[1] || (_cache[1] = e => !_unref(isWeb) ? handleClick : null),
        ref_key: "scrollContainer",
        ref: scrollContainer
      }, [
        _createElementVNode("div", {
          class: "fixed-width",
          style: _normalizeStyle(fixedWidth.value),
          ref_key: "fixedWidthRef",
          ref: fixedWidthRef
        }, [
          (isLoaded.value && __props.zoomable)
            ? (_openBlock(), _createBlock(CustomZoomer, {
                key: 0,
                limitTranslation: false,
                zoomed: isZoomed.value,
                "onUpdate:zoomed": setIsZoomed,
                mouseWheelToZoom: getCanZoomWithMouseWheel.value,
                ref_key: "vZoomerRef",
                ref: vZoomerRef,
                rotation: __props.rotation
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", {
                    style: _normalizeStyle(state.value === State.CENTERED
              ? {
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }
              : undefined
              )
                  }, [
                    _createElementVNode("div", null, [
                      (isZoomed.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_1))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", {
                        ref_key: "pdfContainerRef",
                        ref: pdfContainerRef,
                        id: "pdf-container",
                        style: _normalizeStyle(fixedWidth.value)
                      }, null, 4)
                    ])
                  ], 4)
                ]),
                _: 1
              }, 8, ["zoomed", "mouseWheelToZoom", "rotation"]))
            : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createElementVNode("div", {
                  style: _normalizeStyle(state.value === State.CENTERED
              ? {
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }
              : undefined
              )
                }, [
                  _createElementVNode("div", null, [
                    _createElementVNode("div", {
                      ref_key: "pdfContainerRef",
                      ref: pdfContainerRef,
                      id: "pdf-container",
                      style: _normalizeStyle(fixedWidth.value)
                    }, null, 4)
                  ])
                ], 4)
              ]))
        ], 4)
      ], 38)
    ], 4)
  ], 512))
}
}

})